import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Grid3X3, Settings, ChevronDown, Users, Lock, UserPlus, Globe, MapPin, Calendar, ExternalLink, MessageCircle } from 'lucide-react';
import { User, ProfileSettings } from '../../types';
import { Button } from '../ui/Button';
import { LogoutButton } from '../ui/LogoutButton';
import { FollowButton } from './FollowButton';
import { ProfileSettingsModal } from './ProfileSettingsModal';
import { FollowRequestsModal } from './FollowRequestsModal';
import { useAuthStore } from '../../store/authStore';
import { getFollowCounts } from '../../lib/engagementService';
import { supabase } from '../../lib/supabase';
import { startConversation } from '../../lib/messageService';

interface ProfileHeaderProps {
  user: User;
  isCurrentUser: boolean;
}

export const ProfileHeader: React.FC<ProfileHeaderProps> = ({ user, isCurrentUser }) => {
  const { user: currentUser } = useAuthStore();
  const [followCounts, setFollowCounts] = useState({
    followers: user.followerCount || 0,
    following: user.followingCount || 0
  });
  const [showSettingsModal, setShowSettingsModal] = useState(false);
  const [showFollowRequestsModal, setShowFollowRequestsModal] = useState(false);
  const [profileSettings, setProfileSettings] = useState<ProfileSettings>({
    isPrivate: user.isPrivate || false,
    allowComments: true,
    allowTags: true,
    allowStorySharing: true,
    showActivityStatus: true,
    allowMessageRequests: true,
  });
  const [pendingRequestsCount, setPendingRequestsCount] = useState(0);
  const [startingConversation, setStartingConversation] = useState(false);

  useEffect(() => {
    loadFollowCounts();
    if (isCurrentUser) {
      loadPendingRequestsCount();
    }
  }, [user.id, isCurrentUser]);

  const loadFollowCounts = async () => {
    try {
      const counts = await getFollowCounts(user.id);
      setFollowCounts(counts);
    } catch (error) {
      console.error('Error loading follow counts:', error);
    }
  };

  const loadPendingRequestsCount = async () => {
    try {
      const { count, error } = await supabase
        .from('follow_requests')
        .select('*', { count: 'exact', head: true })
        .eq('requested_id', user.id)
        .eq('status', 'pending');

      if (error) throw error;
      setPendingRequestsCount(count || 0);
    } catch (error) {
      console.error('Error loading pending requests count:', error);
    }
  };

  const handleFollowChange = (isFollowing: boolean) => {
    setFollowCounts(prev => ({
      ...prev,
      followers: isFollowing ? prev.followers + 1 : prev.followers - 1
    }));
  };

  const handleSettingsUpdate = (settings: ProfileSettings) => {
    setProfileSettings(settings);
  };

  const handleMessage = async () => {
    if (!currentUser || isCurrentUser) return;

    setStartingConversation(true);
    try {
      const conversationId = await startConversation(currentUser.id, user.id);
      window.location.href = `/messages/${conversationId}`;
    } catch (error) {
      console.error('Error starting conversation:', error);
      alert('Failed to start conversation. Please try again.');
    } finally {
      setStartingConversation(false);
    }
  };

  const formatJoinDate = (dateString?: string) => {
    if (!dateString) return 'Joined recently';
    const date = new Date(dateString);
    return `Joined ${date.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}`;
  };

  return (
    <>
      <div className="pt-16 pb-4 px-4 md:px-6">
        {/* Profile Header */}
        <div className="flex flex-col md:flex-row md:items-start">
          {/* Profile Image */}
          <div className="flex justify-center md:justify-start md:mr-8 mb-4 md:mb-0">
            <div className="relative">
              <div className="w-20 h-20 md:w-32 md:h-32 rounded-full overflow-hidden border-2 border-white shadow-md">
                <img 
                  src={user.profileImage} 
                  alt={user.displayName} 
                  className="w-full h-full object-cover"
                />
              </div>
              {profileSettings.isPrivate && (
                <div className="absolute -bottom-1 -right-1 bg-gray-800 text-white rounded-full p-1">
                  <Lock className="h-3 w-3" />
                </div>
              )}
            </div>
          </div>
          
          {/* Profile Info */}
          <div className="flex-grow">
            {/* Username and Actions */}
            <div className="flex flex-col md:flex-row md:items-center mb-4">
              <div className="flex items-center mb-2 md:mb-0">
                <h2 className="text-xl font-semibold mr-2">{user.username}</h2>
                {profileSettings.isPrivate && (
                  <Lock className="h-4 w-4 text-gray-500" />
                )}
              </div>
              <div className="flex items-center space-x-2 md:ml-4">
                {isCurrentUser ? (
                  <>
                    <Link to="/edit-profile">
                      <Button variant="outline" size="sm">Edit Profile</Button>
                    </Link>
                    <Button 
                      variant="ghost" 
                      size="sm"
                      onClick={() => setShowSettingsModal(true)}
                    >
                      <Settings className="h-4 w-4" />
                    </Button>
                    {pendingRequestsCount > 0 && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setShowFollowRequestsModal(true)}
                        className="relative"
                      >
                        <UserPlus className="h-4 w-4 mr-1" />
                        Requests
                        <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full h-5 w-5 flex items-center justify-center">
                          {pendingRequestsCount}
                        </span>
                      </Button>
                    )}
                    <LogoutButton variant="full" size="sm" showConfirmation={true} />
                  </>
                ) : (
                  <>
                    <FollowButton
                      userId={user.id}
                      username={user.username}
                      onFollowChange={handleFollowChange}
                      size="sm"
                      isPrivate={profileSettings.isPrivate}
                    />
                    <Button 
                      variant="outline" 
                      size="sm"
                      onClick={handleMessage}
                      disabled={startingConversation}
                      isLoading={startingConversation}
                    >
                      <MessageCircle className="h-4 w-4 mr-1" />
                      Message
                    </Button>
                    <Button variant="ghost" size="sm">
                      <ChevronDown className="h-4 w-4" />
                    </Button>
                  </>
                )}
              </div>
            </div>
            
            {/* Stats */}
            <div className="flex justify-between md:justify-start md:space-x-8 mb-4">
              <div className="text-center md:text-left">
                <span className="font-semibold">{user.postCount}</span> 
                <span className="text-gray-500 text-sm ml-1">posts</span>
              </div>
              <button className="text-center md:text-left hover:text-gray-600 transition-colors">
                <span className="font-semibold">{followCounts.followers}</span> 
                <span className="text-gray-500 text-sm ml-1">followers</span>
              </button>
              <button className="text-center md:text-left hover:text-gray-600 transition-colors">
                <span className="font-semibold">{followCounts.following}</span> 
                <span className="text-gray-500 text-sm ml-1">following</span>
              </button>
            </div>
            
            {/* Bio and Details */}
            <div className="space-y-2">
              <p className="font-semibold">{user.displayName}</p>
              {user.bio && (
                <p className="text-sm whitespace-pre-line">{user.bio}</p>
              )}
              
              {/* Additional Profile Info */}
              <div className="flex flex-wrap gap-4 text-sm text-gray-500">
                {user.location && (
                  <div className="flex items-center">
                    <MapPin className="h-4 w-4 mr-1" />
                    <span>{user.location}</span>
                  </div>
                )}
                {user.website && (
                  <a 
                    href={user.website} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="flex items-center text-purple-600 hover:text-purple-700 transition-colors"
                  >
                    <ExternalLink className="h-4 w-4 mr-1" />
                    <span>Website</span>
                  </a>
                )}
                <div className="flex items-center">
                  <Calendar className="h-4 w-4 mr-1" />
                  <span>{formatJoinDate(user.joinedDate)}</span>
                </div>
              </div>

              {/* Account Type Indicator */}
              <div className="flex items-center text-xs text-gray-500">
                {profileSettings.isPrivate ? (
                  <>
                    <Lock className="h-3 w-3 mr-1" />
                    <span>Private account</span>
                  </>
                ) : (
                  <>
                    <Globe className="h-3 w-3 mr-1" />
                    <span>Public account</span>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
        
        {/* Profile Navigation */}
        <div className="flex justify-around border-t border-gray-200 mt-6">
          <button className="flex items-center justify-center py-3 text-sm font-medium border-t-2 border-purple-600 text-purple-600">
            <Grid3X3 className="h-4 w-4 mr-1" />
            <span>POSTS</span>
          </button>
          <button className="flex items-center justify-center py-3 text-sm font-medium text-gray-500 hover:text-gray-700 transition-colors">
            <span>TAGGED</span>
          </button>
          {isCurrentUser && (
            <Link 
              to="/saved"
              className="flex items-center justify-center py-3 text-sm font-medium text-gray-500 hover:text-gray-700 transition-colors"
            >
              <span>SAVED</span>
            </Link>
          )}
        </div>
      </div>

      {/* Modals */}
      <ProfileSettingsModal
        isOpen={showSettingsModal}
        onClose={() => setShowSettingsModal(false)}
        onSettingsUpdate={handleSettingsUpdate}
      />

      <FollowRequestsModal
        isOpen={showFollowRequestsModal}
        onClose={() => setShowFollowRequestsModal(false)}
      />
    </>
  );
};